import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import GroupChat from '../components/GroupChat';
import { db } from '../firebaseConfig';
import { clearPendingNotificationRedirect } from '../utils/authVerification';

function waitForAuthReady(auth) {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

export default function GroupChatRoute() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeNavigateState = (state) => {
    if (!state || typeof state !== 'object') {
      return null;
    }

    if ('nativeEvent' in state || 'target' in state || 'currentTarget' in state) {
      return null;
    }

    return state;
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const auth = getAuth();
        const authUser = await waitForAuthReady(auth);

        if (cancelled) {
          return;
        }

        if (!authUser) {
          clearPendingNotificationRedirect();
          navigate('/messages', { replace: true });
          return;
        }

        const cached = localStorage.getItem('currentUserProfile');
        const cachedUser = cached ? JSON.parse(cached) : null;
        const me = cachedUser?.uid === authUser.uid ?
          cachedUser :
          { uid: authUser.uid };

        if (!cancelled) {
          setCurrentUser(me);
        }

        const meUid = me?.uid || null;
        if (!groupId || !meUid) {
          clearPendingNotificationRedirect();
          if (!cancelled) {
            navigate('/messages', { replace: true });
          }
          return;
        }

        const groupRef = doc(db, 'conversations', groupId);
        const groupSnap = await getDoc(groupRef);

        if (!groupSnap.exists()) {
          clearPendingNotificationRedirect();
          if (!cancelled) {
            navigate('/messages', { replace: true });
          }
          return;
        }

        const groupData = groupSnap.data();
        if (groupData.deleting === true) {
          clearPendingNotificationRedirect();
          if (!cancelled) {
            const deletedByOther =
              groupData.deleteRequestedBy && groupData.deleteRequestedBy !== meUid;
            navigate('/messages', {
              replace: true,
              state: deletedByOther ? { convoDeleted: true } : null,
            });
          }
          return;
        }

        const members = Array.isArray(groupData.members) ? groupData.members : [];

        if (groupData.isGroup !== true || !members.includes(meUid)) {
          clearPendingNotificationRedirect();
          if (!cancelled) {
            navigate('/messages', { replace: true });
          }
          return;
        }

        clearPendingNotificationRedirect();
      } catch (err) {
        console.error('Failed to load group chat:', err);
        clearPendingNotificationRedirect();
        if (!cancelled) {
          navigate('/messages', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [groupId, navigate]);

  if (loading) {
    return <div style={{ padding: 16 }}>Loading...</div>;
  }

  if (!currentUser) {
    return (
      <div style={{ padding: 16 }}>
        <p>Could not load the group conversation.</p>
        <button onClick={() => navigate('/messages')}>Back to Messages</button>
      </div>
    );
  }

  return (
    <GroupChat
      groupId={groupId}
      onBack={(state = null) => navigate('/messages', {
        replace: true,
        state: normalizeNavigateState(state),
      })}
      refreshGroupList={() => {}}
    />
  );
}
